import { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useControl } from 'react-map-gl/mapbox'
import type { ControlPosition, IControl, MapInstance } from 'react-map-gl/mapbox'

const ZOOM_IN_ICON =
  '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><line x1="12" y1="6" x2="12" y2="18"/><line x1="6" y1="12" x2="18" y2="12"/></svg>'

const ZOOM_OUT_ICON =
  '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><line x1="6" y1="12" x2="18" y2="12"/></svg>'

type ControlLabels = {
  zoomIn: string
  zoomOut: string
}

function createZoomButton(icon: string): HTMLButtonElement {
  const button = document.createElement('button')
  button.type = 'button'
  button.style.alignItems = 'center'
  button.style.color = '#3f4754'
  button.style.display = 'flex'
  button.style.justifyContent = 'center'
  button.innerHTML = icon
  return button
}

class MapZoomControlImpl implements IControl {
  private container: HTMLDivElement
  private zoomInButton: HTMLButtonElement
  private zoomOutButton: HTMLButtonElement
  private map?: MapInstance
  private labels: ControlLabels

  constructor(labels: ControlLabels) {
    this.labels = labels
    this.container = document.createElement('div')
    this.container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group'

    this.zoomInButton = createZoomButton(ZOOM_IN_ICON)
    this.zoomOutButton = createZoomButton(ZOOM_OUT_ICON)

    this.container.append(this.zoomInButton, this.zoomOutButton)
    this.updateLabels()
  }

  onAdd(map: MapInstance): HTMLElement {
    this.map = map
    this.zoomInButton.addEventListener('click', this.handleZoomIn)
    this.zoomOutButton.addEventListener('click', this.handleZoomOut)
    return this.container
  }

  onRemove(): void {
    this.zoomInButton.removeEventListener('click', this.handleZoomIn)
    this.zoomOutButton.removeEventListener('click', this.handleZoomOut)
    this.container.remove()
    this.map = undefined
  }

  private handleZoomIn = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    this.map?.zoomIn()
  }

  private handleZoomOut = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    this.map?.zoomOut()
  }

  private updateLabels() {
    this.zoomInButton.setAttribute('aria-label', this.labels.zoomIn)
    this.zoomInButton.title = this.labels.zoomIn
    this.zoomOutButton.setAttribute('aria-label', this.labels.zoomOut)
    this.zoomOutButton.title = this.labels.zoomOut
  }

  setLabels(labels: ControlLabels) {
    this.labels = labels
    this.updateLabels()
  }
}

type Props = {
  position?: ControlPosition
}

function MapZoomControl({ position = 'bottom-right' }: Props) {
  const intl = useIntl()
  const labels = useMemo(() => ({
    zoomIn: intl.formatMessage({
      id: '7_dPNKcvHmyf2GY2NP5G',
      defaultMessage: 'Zoom in',
      description: 'Accessible label for the Trip Planner map control button that zooms the map in',
    }),
    zoomOut: intl.formatMessage({
      id: 'R8QC5J37u35y0JBviG89',
      defaultMessage: 'Zoom out',
      description: 'Accessible label for the Trip Planner map control button that zooms the map out',
    }),
  }), [intl])

  const control = useControl(
    () => new MapZoomControlImpl(labels),
    { position },
  )

  useEffect(() => {
    control.setLabels(labels)
  }, [control, labels])

  return null
}

export default MapZoomControl
